import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../utils/GlobalAPI';
import AdminPageHeader from './AdminPageHeader';
import EditModal from './EditModal';
import { FaBlog, FaEye, FaEdit, FaTrash } from 'react-icons/fa';

const emptyBlog = {
  heading: '',
  subtext: '',
  content: '',
  image: '',
  type: 'Normal',
};

const AdminBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [form, setForm] = useState(emptyBlog);
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState(emptyBlog);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  useEffect(() => {
    axios
      .get(API_ENDPOINTS.BLOGS)
      .then((res) => setBlogs(res.data))
      .catch((err) => setError(err.message));
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      toBase64(files[0]).then((base) =>
        setForm((prev) => ({ ...prev, image: base }))
      );
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(API_ENDPOINTS.BLOGS, form);
      setBlogs((prev) => [res.data, ...prev]);
      setForm(emptyBlog);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (idx) => {
    setEditIdx(idx);
    setEditForm(blogs[idx]);
  };

  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      toBase64(files[0]).then((base) =>
        setEditForm((prev) => ({ ...prev, image: base }))
      );
    } else {
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditSave = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.put(
        `${API_ENDPOINTS.BLOGS}/${editForm._id}`,
        editForm
      );
      setBlogs((prev) =>
        prev.map((b, i) => (i === editIdx ? res.data : b))
      );
      setEditIdx(null);
      setEditForm(emptyBlog);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseEdit = () => {
    setEditIdx(null);
    setEditForm(emptyBlog);
  };

  const handleDelete = async (idx) => {
    if (!window.confirm('Delete this blog entry?')) return;
    try {
      await axios.delete(
        `${API_ENDPOINTS.BLOGS}/${blogs[idx]._id}`
      );
      setBlogs((prev) => prev.filter((_, i) => i !== idx));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  return (
    <div className="bg-slate-50/50 min-h-screen">
      <AdminPageHeader
        title="Blog Admin Control Panel"
        subtitle="Manage, edit and publish article posts on the CyborgSapient platform"
        icon={<FaBlog className="w-6 h-6" />}
      />

      <div className="pt-32 p-6 max-w-7xl mx-auto space-y-6">
        {error && (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
            <div className="text-rose-600 font-semibold">{error}</div>
          </div>
        )}
        {loading && (
          <div className="bg-gradient-to-r from-orange-50 to-pink-50 border border-orange-200 rounded-xl p-4">
            <div className="text-orange-600 font-medium">Processing request...</div>
          </div>
        )}

        {/* Add Blog Form */}
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h2 className="text-lg font-bold text-slate-800 mb-6">Create New Article</h2>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={handleAdd}>
            <input 
              name="heading" 
              value={form.heading} 
              onChange={handleChange} 
              placeholder="Article Heading" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input 
              name="subtext" 
              value={form.subtext} 
              onChange={handleChange} 
              placeholder="Brief Subtext / Summary" 
              required 
              className="px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 placeholder:text-slate-400 text-sm"
            />
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-orange-50 file:via-pink-50 file:to-purple-50 file:text-orange-600 file:text-xs file:font-bold hover:file:opacity-85 text-sm text-slate-400"
              required
            />
            <select 
              name="type" 
              value={form.type} 
              onChange={handleChange} 
              className="px-4 py-3 border border-slate-200 rounded-xl text-slate-700 focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 bg-white text-sm"
            >
              <option value="Normal">Normal Publication</option>
              <option value="Featured">Featured Article</option>
            </select>
            <textarea 
              name="content" 
              value={form.content} 
              onChange={handleChange} 
              placeholder="Write the full post contents here..." 
              rows={4} 
              required 
              className="md:col-span-2 px-4 py-3 border border-slate-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-orange-500 outline-none transition-all duration-200 resize-none placeholder:text-slate-400 text-sm" 
            />
            <button 
              type="submit" 
              className="col-span-1 md:col-span-2 bg-gradient-to-r from-orange-300 via-pink-300 to-purple-300 hover:opacity-95 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-md text-sm"
            >
              Publish Post
            </button>
          </form>
        </div>

        {/* Blog List */}
        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-600">Published Articles</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Thumbnail</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Article Details</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Body Preview</th>
                  <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {blogs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-16 text-slate-500">
                      <div className="flex flex-col items-center">
                        <div className="w-14 h-14 bg-slate-50 rounded-full flex items-center justify-center mb-3 border border-slate-100">
                          <FaBlog className="w-6 h-6 text-slate-400" />
                        </div>
                        <p className="text-base font-semibold text-slate-700">No blog posts found</p>
                        <p className="text-xs text-slate-400 mt-1">Publish an article to show it here</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  blogs.map((blog, idx) => (
                    <tr key={blog._id} className="hover:bg-slate-50/40 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {blog.image ? (
                          <img src={blog.image} alt="Blog Thumbnail" className="w-20 h-12 object-cover rounded-xl border border-slate-100 shadow-sm animate-fade-in" />
                        ) : (
                          <div className="w-20 h-12 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center">
                            <FaBlog className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-semibold text-slate-800 text-sm">{blog.heading}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5 max-w-[200px] truncate" title={blog.subtext}>{blog.subtext}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-block px-2.5 py-0.5 rounded text-[10px] font-bold ${
                          blog.type === 'Featured' 
                            ? 'bg-purple-50 text-purple-700 border border-purple-200/50' 
                            : 'bg-slate-50 text-slate-600 border border-slate-200/50'
                        }`}>
                          {blog.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-500 text-xs max-w-xs truncate" title={blog.content}>
                        {blog.content}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1.5">
                          <button 
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg border border-transparent hover:border-slate-200 transition-all" 
                            onClick={() => window.open(`/blog/${blog._id}`, '_blank')}
                            title="View Public Post"
                          >
                            <FaEye className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg border border-transparent hover:border-slate-200 transition-all" 
                            onClick={() => handleEditClick(idx)}
                            title="Edit Post Details"
                          >
                            <FaEdit className="w-3.5 h-3.5" />
                          </button>
                          <button 
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg border border-transparent hover:border-rose-100 transition-all" 
                            onClick={() => handleDelete(idx)}
                            title="Remove Post"
                          >
                            <FaTrash className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Modal */}
        <EditModal
          isOpen={editIdx !== null}
          onClose={handleCloseEdit}
          title={`Edit Blog: ${editForm.heading || 'Untitled'}`}
          onSave={handleEditSave}
          saveLabel="Save Changes"
          isLoading={loading}
        >
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm font-semibold">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Image Cover</label>
              <input 
                type="file" 
                name="image" 
                accept="image/*"
                onChange={handleEditChange} 
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-gradient-to-r file:from-orange-50 file:via-pink-50 file:to-purple-50 file:text-orange-600 file:text-xs file:font-bold hover:file:opacity-85 text-sm text-slate-400"
              />
              {editForm.image && (
                <img src={editForm.image} alt="Preview" className="mt-3 w-full h-32 object-cover rounded-xl border border-gray-200 shadow-sm" />
              )}
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Heading *</label>
                <input 
                  name="heading" 
                  value={editForm.heading} 
                  onChange={handleEditChange} 
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Subtext *</label>
                <input 
                  name="subtext" 
                  value={editForm.subtext} 
                  onChange={handleEditChange} 
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Type *</label>
                <select 
                  name="type" 
                  value={editForm.type} 
                  onChange={handleEditChange}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-gray-700 focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 bg-white text-sm"
                >
                  <option value="Normal">Normal</option>
                  <option value="Featured">Featured</option>
                </select>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Content *</label>
              <textarea 
                name="content" 
                value={editForm.content} 
                onChange={handleEditChange} 
                rows={6} 
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl !text-slate-800 !bg-white focus:ring-2 focus:ring-orange-100 focus:border-transparent outline-none transition-all duration-200 resize-none text-sm"
                required
              />
            </div>
          </div>
        </EditModal>
      </div>
    </div>
  );
};

export default AdminBlog;
